import React, { useState, useRef, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, InputGroup } from 'react-bootstrap';
import { 
  FaSearch,
  FaPaperPlane,
  FaPaperclip,
  FaSmile,
  FaCheck,
  FaCheckDouble,
  FaEllipsisV,
  FaUser,
  FaFile,
  FaTimes
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import axios from 'axios';
import '../../../../assets/css/admin/dashboard/AdminChat.css';
import AdminLayout from '../../../../layouts/admin/AdminLayout';
import AppContext from '../../../../context/AppContext';

const AdminChat = () => {

  const { baseUrl } = useContext(AppContext);

  const [conversations, setConversations] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);

  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [messageText, setMessageText] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  const emojis = ['😊', '😂', '❤️', '👍', '🎉', '🙏', '😍', '🔥', '✨', '💯', '👏', '🎊'];

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  
  const viewAllConversations = () => {
    axios.post(`${baseUrl}/admin/chat/viewAllConversations`)
      .then((res) => {
        console.log(res.data);
        if (res.data.success === true) {
          setConversations(res.data.conversations);
          setPageLoading(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setPageLoading(false);
      });
  };

  useEffect(() => {
    viewAllConversations();
  }, []);

  
  const viewMessages = (weconversationId) => {
    const formData = new FormData();
    formData.append('weconversation_id', weconversationId);

    axios.post(`${baseUrl}/admin/chat/viewMessages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then((res) => {
        console.log(res.data);
        if (res.data.success === true) {
          setMessages(res.data.messages);
          markAsRead(weconversationId);
        }
      })
      .catch((err) => console.log(err));
  };

  
  const markAsRead = (weconversationId) => {
    const formData = new FormData();
    formData.append('weconversation_id', weconversationId);

    axios.post(`${baseUrl}/admin/chat/markAsRead`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then((res) => {
        console.log(res.data);
        setConversations((prev) =>
          prev.map((c) =>
            c.weconversation_id === weconversationId ? { ...c, unreadCount: 0 } : c
          )
        );
      })
      .catch((err) => console.log(err));
  };

  const handleSelectConversation = (weconversationId) => {
    setSelectedConversationId(weconversationId);
    viewMessages(weconversationId);
  };

  
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedConversationId) return;

    const formData = new FormData();
    formData.append('weconversation_id', selectedConversationId);
    formData.append('message', messageText);

    axios.post(`${baseUrl}/admin/chat/sendMessage`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then((res) => {
        console.log(res.data);
        if (res.data.success === true) {
          viewMessages(selectedConversationId);
          viewAllConversations();
          setMessageText('');
          setUploadedFile(null);
          setShowEmojiPicker(false);
        }
      })
      .catch((err) => console.log(err));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'Please upload a file smaller than 5MB',
          confirmButtonColor: '#7fd3c1'
        });
        return;
      }
      setUploadedFile({
        name: file.name,
        size: (file.size / 1024).toFixed(2) + ' KB',
        type: file.type,
        url: URL.createObjectURL(file)
      });
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEmojiClick = (emoji) => {
    setMessageText(messageText + emoji);
    setShowEmojiPicker(false);
  };

  // Filter Customers
  const filteredConversations = conversations.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.lastMessage || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get Selected Conversation
  const selectedConversation = conversations.find(c => c.weconversation_id === selectedConversationId);

  // Calculate Total Unread
  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return (
    <AdminLayout>
    <div className="ach-admin-chat-page">
      <Container fluid className="ach-container">

        {/* Page Header */}
        <div className="ach-page-header">
          <div className="ach-header-left">
            <h1 className="ach-page-title">
              <FaUser className="ach-title-icon" />
              Customer Messages
            </h1>
            <p className="ach-page-subtitle">Manage customer inquiries and support</p>
          </div>
          <div className="ach-header-right">
            {totalUnread > 0 && (
              <Badge bg="danger" className="ach-total-unread-badge">
                {totalUnread} Unread
              </Badge>
            )}
          </div>
        </div>

        {/* Chat Container */}
        <Card className="ach-chat-container">
          {pageLoading ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div className="spinner-border" role="status" style={{ color: '#7fd3c1' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: '12px', color: '#888' }}>Loading conversations...</p>
            </div>
          ) : (
          <Row className="g-0 h-100">

            {/* Left Side - Customer List */}
            <Col lg={4} md={5} className="ach-customer-list-col">
              <div className="ach-customer-list">

                {/* Search */}
                <div className="ach-search-wrapper">
                  <InputGroup>
                    <InputGroup.Text className="ach-search-icon">
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search customers..."
                      className="ach-search-input"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </div>

                {/* Customers List */}
                <div className="ach-customers">
                  {filteredConversations.length === 0 ? (
                    <p className="text-center text-muted p-3">No conversations yet</p>
                  ) : (
                    filteredConversations.map((conversation) => (
                      <div
                        key={conversation.weconversation_id}
                        className={`ach-customer-item ${selectedConversationId === conversation.weconversation_id ? 'ach-active' : ''}`}
                        onClick={() => handleSelectConversation(conversation.weconversation_id)}
                      >
                        <div className="ach-avatar-wrapper">
                          <img
                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.name)}&background=7fd3c1&color=fff`}
                            alt={conversation.name}
                            className="ach-avatar"
                          />
                        </div>
                        <div className="ach-customer-info">
                          <div className="ach-customer-header">
                            <h6 className="ach-customer-name">{conversation.name}</h6>
                            <span className="ach-customer-time">{formatTime(conversation.time)}</span>
                          </div>
                          <div className="ach-customer-footer">
                            <p className="ach-last-message">{conversation.lastMessage || 'No messages yet'}</p>
                            {conversation.unreadCount > 0 && (
                              <Badge bg="danger" pill className="ach-unread-badge">
                                {conversation.unreadCount}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </Col>

            {/* Right Side - Chat Window */}
            <Col lg={8} md={7} className="ach-chat-window-col">
              <div className="ach-chat-window">

                {selectedConversation ? (
                  <>
                    {/* Chat Header */}
                    <div className="ach-chat-header">
                      <div className="ach-chat-customer-info">
                        <img
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(selectedConversation.name)}&background=7fd3c1&color=fff`}
                          alt={selectedConversation.name}
                          className="ach-chat-avatar"
                        />
                        <div className="ach-chat-customer-details">
                          <h5 className="ach-chat-customer-name">
                            Customer: {selectedConversation.name}
                          </h5>
                        </div>
                      </div>
                      <button className="ach-options-btn">
                        <FaEllipsisV />
                      </button>
                    </div>

                    {/* Messages Area */}
                    <div className="ach-messages-area">
                      {messages.length === 0 ? (
                        <p className="text-center text-muted">No messages yet</p>
                      ) : (
                        messages.map((message) => (
                          <div
                            key={message.id}
                            className={`ach-message ${message.sender === 'admin' ? 'ach-message-admin' : 'ach-message-customer'}`}
                          >
                            <div className="ach-message-content">
                              {message.message && <p className="ach-message-text">{message.message}</p>}
                              <div className="ach-message-meta">
                                <span className="ach-message-sender">
                                  {message.sender === 'admin' ? 'You' : selectedConversation.name}
                                </span>
                                <span className="ach-message-time">{formatTime(message.created_at)}</span>
                                {message.sender === 'admin' && (
                                  <span className="ach-message-status">
                                    {message.status === 'read' ? (
                                      <FaCheckDouble className="ach-read" />
                                    ) : (
                                      <FaCheck className="ach-delivered" />
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                      <div ref={chatEndRef} />
                    </div>

                    {/* Message Input */}
                    <div className="ach-message-input-wrapper">
                      {uploadedFile && (
                        <div className="ach-file-upload-preview">
                          {uploadedFile.type.startsWith('image/') ? (
                            <img
                              src={uploadedFile.url}
                              alt={uploadedFile.name}
                              className="ach-preview-image"
                            />
                          ) : (
                            <div className="ach-preview-file">
                              <FaFile className="ach-preview-icon" />
                              <div className="ach-preview-info">
                                <span className="ach-preview-name">{uploadedFile.name}</span>
                                <span className="ach-preview-size">{uploadedFile.size}</span>
                              </div>
                            </div>
                          )}
                          <button
                            className="ach-remove-file-btn"
                            onClick={handleRemoveFile}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}

                      <Form onSubmit={handleSendMessage} className="ach-message-form">
                        <div className="ach-input-actions">
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                            accept="image/*,.pdf,.doc,.docx"
                          />
                          <Button
                            variant="link"
                            className="ach-action-btn"
                            onClick={() => fileInputRef.current?.click()}
                            title="Attach File"
                          >
                            <FaPaperclip />
                          </Button>
                          <div className="ach-emoji-picker-wrapper">
                            <Button
                              variant="link"
                              className="ach-action-btn"
                              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                              title="Add Emoji"
                            >
                              <FaSmile />
                            </Button>
                            {showEmojiPicker && (
                              <div className="ach-emoji-picker">
                                {emojis.map((emoji, index) => (
                                  <span
                                    key={index}
                                    className="ach-emoji"
                                    onClick={() => handleEmojiClick(emoji)}
                                  >
                                    {emoji}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <Form.Control
                          type="text"
                          placeholder="Type a reply..."
                          className="ach-message-input"
                          value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                        />
                        <Button
                          type="submit"
                          className="ach-send-btn"
                          disabled={!messageText.trim()}
                        >
                          <FaPaperPlane />
                        </Button>
                      </Form>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted p-5">Select a conversation to start chatting</p>
                )}
              </div>
            </Col>
          </Row>
          )}
        </Card>

      </Container>
    </div>
    </AdminLayout>
  );
};

export default AdminChat;